import i18n from 'i18next'

// Non-React module called from services/handlers with no component context,
// so `useTranslation` isn't available. Call `i18n.t()` directly; i18n is
// already initialized at app bootstrap.
const excludedMessages = [/login required/i, /Network error/i]

// i18next's generated key types only accept compile-time-known keys. Backend
// error codes are runtime strings, so this module needs a narrow escape hatch
// for dynamic lookups at the API boundary.
const translateDynamicKey = i18n.t as unknown as (key: string) => string
const getDynamicTranslator = i18n.getFixedT as unknown as (
  lng: null,
  ns: string,
) => (key: string) => string

interface GraphQLError {
  message: string
}

interface NetworkError {
  error_description?: string
}

interface ErrorInput {
  graphQLErrors?: GraphQLError[]
  networkError?: NetworkError
  message?: string
  errorCode?: string
}

const translateErrorCode = (errorCode: string): string => {
  const [namespace, ...keyParts] = errorCode.split('.')

  if (!namespace || !keyParts.length) {
    return translateDynamicKey(errorCode)
  }

  const key = keyParts.join('.')
  return getDynamicTranslator(null, namespace)(key)
}

const ErrorHandler = (error: ErrorInput, extra?: unknown) => {
  const errorMessage =
    (error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) ||
    (error.networkError && error.networkError.error_description) ||
    error.message ||
    'Unknown error'
  const message: string = i18n.t('errors.generic_retry', { ns: 'common' })

  for (const excludedMessage of excludedMessages) {
    if (
      excludedMessage.test(errorMessage) ||
      (error.message && excludedMessage.test(error.message))
    ) {
      return { message, errorMessage }
    }
  }

  console.error('ErrorHandler', error, message, extra)

  let translatedErrorMessage = errorMessage

  if (error.errorCode) {
    try {
      translatedErrorMessage = translateErrorCode(error.errorCode)
      const fallbackKey = error.errorCode.includes('.')
        ? error.errorCode.split('.').slice(1).join('.')
        : error.errorCode
      if (translatedErrorMessage === fallbackKey || translatedErrorMessage === error.errorCode) {
        translatedErrorMessage = errorMessage
      }
    } catch {
      translatedErrorMessage = errorMessage
    }
  }

  return { message, errorMessage: translatedErrorMessage }
}

export default ErrorHandler
